// Shared "load an existing item's configuration" logic, used by both the manual
// Item Template field and the Edit Config auto-load (AutoLoadItem).
//
// Order of preference:
//   1. Full snapshot (custitem_cpq2configuration) — restores everything,
//      including the top-level inputs the mapping doesn't encode.
//   2. Mapping (custitem_mapping) — a PARTIAL restore of the manufacturing
//      config. Used when there is no snapshot, OR when the snapshot exists but
//      is unreadable (e.g. an item last saved by the original NetSuite CPQ,
//      whose config field is a different format, or a truncated snapshot).
// This fallback is what keeps Edit Config working on legacy items instead of
// dead-ending on "Stored configuration is not valid JSON".
//
// INPUT RESOLUTION: the Item Template accepts EITHER an item number / internal id
// OR a Customer Part Number. We try it as an item first (unchanged behavior); if
// no such item exists, we resolve it as a Customer Part Number
// (customrecord_scm_customerpartnumber — a globally-unique name) and load the item
// that holds it. Item numbers therefore always win a collision, and the extra
// lookup only happens when the input isn't a known item.

import { useAnswers } from "./AnswersContext";
import { fetchItemMapping, checkCustomerPartNumber } from "../util/netsuiteClient";

// `cannotEdit` echoes the item's custitem_cannoteditcpq so the caller (the Edit
// Config auto-load) can switch to New Config when the launched item is locked.
// `itemInternalId` is the loaded item's NetSuite internal id — the Item Template
// caller uses it to switch into Edit mode (update THAT item in place) when the
// item is editable.
export type ItemLoadResult = {
  kind: "ok" | "err";
  text: string;
  cannotEdit?: boolean;
  /** Why it cannot be edited, when it cannot (the server's words). */
  lockReason?: string | null;
  itemInternalId?: string | null;
};

export function useItemLoader() {
  const { loadItemConfig, loadItemTemplate } = useAnswers();

  // Load a resolved item (by number or internal id). `viaCpn` is the Customer
  // Part Number the caller matched when the item was reached that way — used only
  // to enrich the status message. THROWS when the item itself can't be found, so
  // loadItem can fall back to a Customer Part Number lookup.
  async function loadResolvedItem(
    itemNumber: string,
    viaCpn: string | null
  ): Promise<ItemLoadResult> {
    const res = await fetchItemMapping(itemNumber);
    const base = res.itemid ?? itemNumber;
    const label = viaCpn ? `${base} (customer part # ${viaCpn})` : base;
    const cannotEdit = res.cannotEdit === true;
    const lockReason = res.lockReason ?? null;
    const itemInternalId = res.id != null ? String(res.id) : null;

    // The editable Inlet Connection Notes field maps back from the item field
    // custiteminlet_connection_notes (source of truth), filling any gap the
    // snapshot doesn't already carry.
    const fieldOverrides = res.inletNotes
      ? { "INLET_CONNECTION_NOTES/INLET_CONNECTION_NOTES": res.inletNotes }
      : undefined;

    let configError: string | null = null;
    if (res.config) {
      try {
        const { count } = await loadItemConfig(
          res.config,
          fieldOverrides,
          // Records what this item holds, so an edit can say what it changed.
          itemInternalId ? { itemId: itemInternalId, itemName: res.itemid ?? null } : undefined
        );
        return { kind: "ok", text: `Loaded ${label} — full configuration (${count} fields).`, cannotEdit, lockReason, itemInternalId };
      } catch (e) {
        // Snapshot present but not readable — remember why, then try the mapping.
        configError = e instanceof Error ? e.message : String(e);
      }
    }

    if (res.mapping) {
      const { selects, values } = loadItemTemplate(res.mapping);
      const note = configError
        ? "saved config unreadable — used mapping (partial)"
        : res.mappingFrom === "fields"
          ? "built from the item's record fields; it had no saved configuration or mapping — set the housing on page 1"
          : "partial; item has no saved config";
      return { kind: "ok", text: `Loaded ${label} from mapping — ${selects + values} fields (${note}).`, cannotEdit, lockReason, itemInternalId };
    }

    if (configError) {
      return { kind: "err", text: `Saved configuration for ${label} is unreadable (${configError}), and it has no mapping to fall back to.`, cannotEdit, itemInternalId };
    }
    return { kind: "err", text: `Item ${label} has no configuration to load.`, cannotEdit, itemInternalId };
  }

  return async function loadItem(input: string): Promise<ItemLoadResult> {
    try {
      // Primary: treat the input as an item number / internal id (unchanged).
      return await loadResolvedItem(input, null);
    } catch (itemErr) {
      // Not a known item — try it as a Customer Part Number and load the holder.
      const cpn = await checkCustomerPartNumber(input); // null on any proxy failure
      const holder =
        cpn && cpn.exists ? cpn.holders.find((h) => h.itemId || h.itemName) : null;
      if (holder && (holder.itemId || holder.itemName)) {
        return await loadResolvedItem(String(holder.itemId || holder.itemName), input);
      }
      throw itemErr; // neither a valid item number nor a known customer part number
    }
  };
}
